import React, { useState } from "react";
import {
  AlertTriangle,
  Button,
  CheckCircle,
  MessageCircle,
  RefreshCcw,
  Send,
  Trash2,
  Unlink,
  Utensils,
  XCircle,
  Link as LinkIcon,
  History,
} from "../../../ui";
import { tx } from "../../ludConnect.constants";
import {
  elapsedInfo,
  fmtDate,
  requestTypeInfo,
  statusInfo,
} from "../../helpers/ludConnect.helpers";
import "./RequestsPanel.css";

const TYPE_ICONS = {
  link: LinkIcon,
  unlink: Unlink,
  activate: CheckCircle,
  close: XCircle,
  menu: Utensils,
  issue: AlertTriangle,
};

const getRequestSortTime = (request = {}) => {
  const value = request.deleted
    ? request.deletedAt || request.updatedAt || request.createdAt
    : request.createdAt || request.updatedAt;

  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortRequestsNewestFirst = (items = []) =>
  [...items].sort(
    (first, second) =>
      getRequestSortTime(second) - getRequestSortTime(first),
  );

const getActivitySortTime = (item = {}) => {
  const time = new Date(item.createdAt || item.rawDate || item.date || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const DetailLine = ({ label, children }) => (
  <>
    {label}: <strong>{children}</strong>
    <br />
  </>
);

const RequestDetails = ({ ar, request }) => {
  const payload = request.payload || {};

  return (
    <div className="details">
      {request.type === "link_app" && (
        <>
          <DetailLine label={tx("التطبيق", "Application", ar)}>
            {payload.appName || "—"}
          </DetailLine>
          <DetailLine
            label={tx("اسم الفرع بالتطبيق", "Branch name in application", ar)}
          >
            {payload.appBranchName || "—"}
          </DetailLine>
          <DetailLine label={tx("المعرف", "ID", ar)}>
            {payload.credId || "—"}
          </DetailLine>
        </>
      )}

      {request.type === "disconnect_app" && (
        <>
          <DetailLine label={tx("التطبيق", "Application", ar)}>
            {payload.appName || "—"}
          </DetailLine>
          {payload.note && (
            <DetailLine label={tx("السبب", "Reason", ar)}>
              {payload.note}
            </DetailLine>
          )}
        </>
      )}

      {request.type === "activate_branch" && (
        <DetailLine label={tx("النطاق", "Scope", ar)}>
          {tx("تفعيل الفرع", "Branch activation", ar)}
        </DetailLine>
      )}

      {request.type === "close_branch" && (
        <>
          <DetailLine label={tx("النطاق", "Scope", ar)}>
            {tx(
              "إغلاق الفرع بالكامل — كل التطبيقات",
              "Close the entire branch — all applications",
              ar,
            )}
          </DetailLine>
          {payload.note && (
            <DetailLine label={tx("السبب", "Reason", ar)}>
              {payload.note}
            </DetailLine>
          )}
        </>
      )}

      {request.type === "issue" && (
        <>
          {payload.appName && (
            <DetailLine label={tx("التطبيق", "Application", ar)}>
              {payload.appName}
            </DetailLine>
          )}
          <DetailLine label={tx("الوصف", "Description", ar)}>
            {payload.description || "—"}
          </DetailLine>
        </>
      )}

      {request.type === "menu_update" && (
        <>
          {payload.scope === "full" && (
            <DetailLine label={tx("النطاق", "Scope", ar)}>
              {tx("القائمة كاملة", "Full menu", ar)}
            </DetailLine>
          )}
          {payload.scope === "add" && payload.item && (
            <>
              <DetailLine
                label={tx("إضافة صنف جديد", "Add new item", ar)}
              >{`${payload.item.name} (${payload.item.price} ${tx("ر.س", "SAR", ar)})`}</DetailLine>
              {payload.item.desc && (
                <DetailLine label={tx("الوصف", "Description", ar)}>
                  {payload.item.desc}
                </DetailLine>
              )}
              <DetailLine label={tx("السعرات الحرارية", "Calories", ar)}>
                {payload.item.cal || 0}
              </DetailLine>
            </>
          )}
          {payload.scope === "multi" && (
            <DetailLine label={tx("الأصناف", "Items", ar)}>
              {(payload.items || [])
                .map((item) => item.name)
                .join(tx("، ", ", ", ar)) || "—"}
            </DetailLine>
          )}
          {payload.scope === "item" && (
            <DetailLine label={tx("الصنف", "Item", ar)}>
              {payload.itemName || "—"}
            </DetailLine>
          )}
          {payload.comment && (
            <DetailLine label={tx("التعليق", "Comment", ar)}>
              {payload.comment}
            </DetailLine>
          )}
        </>
      )}
    </div>
  );
};

const RequestCard = ({ ar, request, isProvider, pendingActions, onAction }) => {
  const elapsed = elapsedInfo(request, ar);
  const type = requestTypeInfo(request.type, ar);
  const TypeIcon = TYPE_ICONS[type.icon] || AlertTriangle;

  if (request.deleted) {
    return (
      <div className="lud-connect__request-card">
        <div className="top">
          <span>
            <TypeIcon size={14} /> {type.label}
          </span>
        </div>
        <b>
          {request.payload.branchName || request.payload.restName}{" "}
          <small>({request.id})</small>
        </b>
        <small>
          {tx("حُذف بتاريخ", "Deleted on", ar)} {fmtDate(request.deletedAt, ar)}
        </small>
        <div className="actions">
          <Button
            size="sm"
            variant="danger"
            appearance="filled"
            icon={<RefreshCcw size={14} />}
            loading={pendingActions?.has("restore:" + request.id)}
            onClick={() => onAction(request.id, "restore")}
          >
            {tx("تراجع عن الحذف", "Restore", ar)}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="lud-connect__request-card">
      <div className="top">
        <span>
          <TypeIcon size={14} /> {type.label}
        </span>
        <em className={elapsed.className}>{elapsed.text}</em>
      </div>

      <b>
        {request.payload.restName}
        {request.payload.branchName
          ? ` — ${request.payload.branchName}`
          : ""}{" "}
        <small>({request.id})</small>
      </b>

      <RequestDetails ar={ar} request={request} />

      <div className="status">
        <span>{statusInfo(request.status, ar).label}</span>
        <small>{fmtDate(request.createdAt, ar)}</small>
      </div>

      {request.history?.length > 0 && (
        <div className="lud-connect__request-last-note">
          {tx("آخر تحديث", "Last update", ar)}:{" "}
          {request.history[request.history.length - 1].label}
        </div>
      )}

      {request.comments?.length > 0 && (
        <div className="lud-connect__request-comments">
          {request.comments.map((comment, index) => (
            <div key={`${request.id}-comment-${index}`}>
              <b>{comment.actor}</b>
              <small>{fmtDate(comment.date, ar)}</small>
              <p>{comment.text}</p>
            </div>
          ))}
        </div>
      )}

      {request.status === "rejected" && request.reason && (
        <div className="lud-connect__request-reason">
          {tx("سبب الرفض", "Rejection reason", ar)}: {request.reason}
        </div>
      )}
      {request.status === "clarify" && request.note && (
        <div className="lud-connect__request-clarify">
          {tx("التوضيح المطلوب", "Required clarification", ar)}: {request.note}
        </div>
      )}

      <div className="actions">
        {!isProvider && request.status === "draft" && (
          <>
            <Button
              size="sm"
              variant="primary"
              appearance="filled"
              icon={<Send size={14} />}
              onClick={() => onAction(request.id, "send")}
            >
              {tx("إرسال", "Send", ar)}
            </Button>
            <Button
              size="sm"
              variant="danger"
              appearance="filled"
              icon={<Trash2 size={14} />}
              onClick={() => onAction(request.id, "delete")}
            >
              {tx("حذف", "Delete", ar)}
            </Button>
          </>
        )}
        {!isProvider && request.status === "clarify" && (
          <Button
            size="sm"
            variant="warning"
            onClick={() => onAction(request.id, "resend")}
          >
            {tx("إعادة الإرسال", "Resend", ar)}
          </Button>
        )}
        {isProvider && request.status === "sent" && (
          <>
            <Button
              size="sm"
              variant="success"
              onClick={() => onAction(request.id, "accept")}
            >
              {tx("قبول", "Accept", ar)}
            </Button>
            <Button
              size="sm"
              variant="danger"
              appearance="filled"
              onClick={() => onAction(request.id, "reject")}
            >
              {tx("رفض", "Reject", ar)}
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() => onAction(request.id, "clarify")}
            >
              {tx("طلب توضيح", "Request clarification", ar)}
            </Button>
          </>
        )}
        {isProvider && request.status === "req_in_progress" && (
          <Button
            size="sm"
            variant="success"
            onClick={() => onAction(request.id, "finish")}
          >
            {tx("تنفيذ مكتمل", "Execution complete", ar)}
          </Button>
        )}
        {isProvider && request.status === "pending_confirm" && (
          <span className="lud-connect__muted">
            {tx("بانتظار اعتماد LUD", "Waiting for LUD confirmation", ar)}
          </span>
        )}
        {!isProvider && request.status === "pending_confirm" && (
          <>
            <Button
              size="sm"
              variant="success"
              onClick={() => onAction(request.id, "confirm")}
            >
              {tx("تأكيد الإنجاز", "Confirm completion", ar)}
            </Button>
            <Button
              size="sm"
              variant="neutral"
              onClick={() => onAction(request.id, "reopen")}
            >
              {tx("إعادة فتح", "Reopen", ar)}
            </Button>
          </>
        )}
        {!statusInfo(request.status, ar).final &&
          request.status !== "draft" && (
            <Button
              size="sm"
              variant="neutral"
              icon={<MessageCircle size={14} />}
              onClick={() => onAction(request.id, "comment")}
            >
              {tx("تعليق", "Comments", ar)}
            </Button>
          )}
      </div>
    </div>
  );
};

const Box = ({ ar, title, icon: Icon, requests, isProvider, pendingActions, onAction }) => {
  const [open, setOpen] = useState(true);
  const opened = requests.filter(
    (request) => !request.deleted && !statusInfo(request.status, ar).final,
  ).length;
  const overdue = requests.filter(
    (request) =>
      !request.deleted &&
      !statusInfo(request.status, ar).final &&
      elapsedInfo(request, ar).className === "late",
  ).length;
  const openedRequests = sortRequestsNewestFirst(
    requests.filter(
      (request) => !statusInfo(request.status, ar).final,
    ),
  );
  const closedRequests = sortRequestsNewestFirst(
    requests.filter(
      (request) => statusInfo(request.status, ar).final,
    ),
  );

  return (
    <div className="lud-connect__request-box">
      <button
        type="button"
        className="lud-connect__request-head"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <b>
          <Icon size={16} /> {title}
        </b>
        <span>
          {requests.length} {tx("إجمالي", "total", ar)} · {opened}{" "}
          {tx("مفتوحة", "open", ar)}
          {overdue > 0
            ? ` · ${overdue} ${tx("متأخرة", "overdue", ar)}`
            : ""}{" "}
          {open ? "▴" : "▾"}
        </span>
      </button>
      {open && (
        <div>
          {openedRequests.length ? (
            openedRequests.map((request) => (
              <RequestCard
                key={request.id}
                ar={ar}
                request={request}
                isProvider={isProvider}
                pendingActions={pendingActions}
                onAction={onAction}
              />
            ))
          ) : (
            <span className="lud-connect__muted">
              {tx("لا يوجد طلبات مفتوحة", "No open requests", ar)}
            </span>
          )}
          {closedRequests.length > 0 && (
            <>
              <div className="lud-connect__request-section-label">
                {tx("منتهية", "Completed", ar)}
              </div>
              {closedRequests.map((request) => (
                <RequestCard
                  key={request.id}
                  ar={ar}
                  request={request}
                  isProvider={isProvider}
                  pendingActions={pendingActions}
                  onAction={onAction}
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const RequestsPanel = ({ ar, requests, isProvider, pendingActions, onAction, logs = [] }) => {
  const [activityOpen, setActivityOpen] = useState(false);
  const sortedLogs = [...logs].sort(
    (first, second) =>
      getActivitySortTime(second) - getActivitySortTime(first),
  );

  return (
  <div className="lud-connect__requests-side">
    <Box
      ar={ar}
      title={tx("طلبات الربط الجديد", "New connection requests", ar)}
      icon={LinkIcon}
      requests={requests.filter(
        (request) =>
          [
            "link_app",
            "disconnect_app",
            "activate_branch",
            "close_branch",
          ].includes(request.type) && !request.deleted,
      )}
      isProvider={isProvider}
      pendingActions={pendingActions}
      onAction={onAction}
    />
    <Box
      ar={ar}
      title={tx("التعديلات والمشاكل", "Edits and issues", ar)}
      icon={AlertTriangle}
      requests={requests.filter(
        (request) =>
          ["menu_update", "issue"].includes(request.type) && !request.deleted,
      )}
      isProvider={isProvider}
      pendingActions={pendingActions}
      onAction={onAction}
    />
    {!isProvider && (
      <Box
        ar={ar}
        title={tx("المحذوفات", "Deleted", ar)}
        icon={Trash2}
        requests={requests.filter((request) => request.deleted)}
        isProvider={isProvider}
        pendingActions={pendingActions}
        onAction={onAction}
      />
    )}
    {!isProvider &&<div className="lud-connect__activity-dropdown">
      <button
        type="button"
        className="lud-connect__activity-toggle"
        aria-expanded={activityOpen}
        onClick={() => setActivityOpen((current) => !current)}
      >
        <span><History size={15} /> {tx("سجل النشاط", "Activity log", ar)}</span>
        <b>{activityOpen ? "▴" : "▾"}</b>
      </button>
      {activityOpen && (
        <div className="lud-connect__activity-list">
          {sortedLogs.length ? sortedLogs.map((log, index) => (
            <div className="lud-connect__log" key={`${log.date}-${index}`}>
              <span>{log.date}</span>
              <b>{log.actor}</b>
              <p>{log.action}</p>
            </div>
          )) : (
            <div className="lud-connect__muted">
              {tx("لا يوجد نشاط مسجّل بعد", "No activity recorded yet", ar)}
            </div>
          )}
        </div>
      )}
    </div>}
  </div>
  );
};

export default RequestsPanel;
